import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  CompetenciesCompetencyCrumbDocument,
  CompetenciesCompetencyLevelsCreateSidepanelDocument,
  CompetencyCompetencyLevelFragment,
  CompetencyLevelsDataCollectionDocument,
  CompetencyLevelsPageDocument,
} from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

import Sidepanel from 'modules/core/components/Sidepanel'
import { type FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  CompetencyLevelsForm,
  type LevelData,
  validateLevels,
} from 'modules/competencies/components/CompetencyLevelsForm'

type FormValues = { levels: LevelData[] }
type Props = {
  open: boolean
  competencyId: string
  existingLevelsCount?: number
  onSuccess: () => void
  onClose: () => void
  existingLevels: CompetencyCompetencyLevelFragment[]
}

export const CompetencyLevelsCreateSidepanel = ({
  open,
  competencyId,
  existingLevelsCount = 0,
  onSuccess,
  onClose,
  existingLevels,
}: Props) => {
  const [levelsAtSubmit, setLevelsAtSubmit] = useState<Set<string | number>>(() => new Set())

  const [createLevels, { loading }] = useMutation(
    CompetenciesCompetencyLevelsCreateSidepanelDocument,
    {
      extractorFn: (data) => data.competenciesMutations.bulkCreateCompetenciesCompetencyLevel,
    }
  )

  const form = useForm<FormValues>({
    defaultValues: {
      levels: [{ id: uuid(), name: '', description: '' }],
    },
    validate: (values) => {
      const errors: FormErrors<FormValues> = {}

      const { errors: levelErrors, hasError } = validateLevels(values.levels, existingLevels)
      if (hasError) {
        errors.levels = Object.values(levelErrors)
      }

      return errors
    },
    onSubmit: async (values) => {
      const result = await createLevels({
        variables: {
          competencyId: competencyId,
          levels: values.levels.map((v, index) => ({
            name: v.name,
            description: v.description,
            position: existingLevelsCount + index,
          })),
        },
        refetchQueries: [
          CompetencyLevelsDataCollectionDocument,
          CompetencyLevelsPageDocument,
          CompetenciesCompetencyCrumbDocument,
        ],
      })
      const handled = handleResult(result, (errors) => {
        if (Object.keys(errors).length > 0) {
          form.setErrors(errors)
        }
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('competencies_v3.levels.create.created_successfully'),
        })
        form.resetForm()
        setLevelsAtSubmit(new Set())
        onSuccess()
      }
      return handled
    },
  })

  const handleClose = () => {
    form.resetForm()
    onClose()
  }

  const handleShowError = (_field: keyof LevelData, level: LevelData) => {
    if (form.submitCount === 0) {
      return false
    }

    return levelsAtSubmit?.has(level.id)
  }

  return (
    <Sidepanel
      open={open}
      onClose={onClose}
      onCloseClicked={handleClose}
      width="wideXL"
      title={i18n.t('competencies_v3.levels.create.title')}
      footer={
        <>
          <Button onClick={handleClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              setLevelsAtSubmit(new Set(form.values.levels.map((level) => level.id)))
              form.handleSubmit()
            }}
            loading={form.isSubmitting}
            disabled={form.values.levels.length === 0}
          >
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <CompetencyLevelsForm
        levels={form.values.levels}
        errors={form.errors.levels}
        onShowError={handleShowError}
        loading={loading}
        existingLevelsCount={existingLevelsCount}
        allowAdd
        allowDelete
        onLevelChange={(values) => form.setFieldValue('levels', values)}
        onLevelAdd={(values) => form.setFieldValue('levels', values)}
        onLevelDelete={(values) => form.setFieldValue('levels', values)}
      />
    </Sidepanel>
  )
}
